import { createContext, useContext, useRef, useCallback, useSyncExternalStore } from 'react';
import type { ReactNode, JSX } from 'react';
import type { MockCache } from '../types';

import { activeCache, getVersion, requireCache, setActiveCache, subscribeKeys } from './runtime';

const MockStoreContext = createContext<MockCache | null>(null);

export interface MockStoreProviderProps {
  cache: MockCache;
  children: ReactNode;
}

export function MockStoreProvider({ cache, children }: MockStoreProviderProps): JSX.Element {
  setActiveCache(cache);
  return <MockStoreContext.Provider value={cache}>{children}</MockStoreContext.Provider>;
}

export function useCache(): MockCache {
  const fromContext = useContext(MockStoreContext);
  if (fromContext !== null) {
    return fromContext;
  }
  return requireCache();
}

export function useCacheValue<T>(keys: string[], selector: (cache: MockCache) => T): T {
  const cache = useCache();
  const keysKey = keys.join('\u0000');
  const selectorRef = useRef(selector);
  selectorRef.current = selector;
  const memoRef = useRef<{ sig: string; value: T } | null>(null);
  const subscribe = useCallback(
    (notify: () => void): (() => void) => {
      const list = keysKey.split('\u0000').filter((key) => key.length > 0);
      return subscribeKeys(list, notify);
    },
    [keysKey],
  );
  const getSnapshot = useCallback((): T => {
    const current = activeCache ?? cache;
    const list = keysKey.split('\u0000').filter((key) => key.length > 0);
    const sig = list.map((key) => `${key}@${getVersion(key)}`).join('|');
    const prev = memoRef.current;
    if (prev !== null && prev.sig === sig) {
      return prev.value;
    }
    const next = selectorRef.current(current);
    memoRef.current = { sig, value: next };
    return next;
  }, [cache, keysKey]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
